import React, { ReactElement, memo, useMemo } from 'react';
import styled from '@emotion/styled';
import { getTextColor } from '../../../style';
import { vysionTrack } from '../../../utils/vysion/vysionService';
import { lazilyEvaluateVal, ternaryFunc } from '../../../utils/helpers';
import { renderAvatar } from '../../../common';
import FavoriteCard from '../../../atoms/FavoriteCard';
import FavoriteActionButton from './FavoriteActionButton';
import useMixPanel from '../../../hooks/useMixPanel';
import { EVENT_NAMES, TRACK_PROFILE_VIEWS_FOR_OFFER_MADE } from '../../../constants/mixpanel';
import { useDispatch } from 'react-redux';
import { favToggle, storeFavChange } from '../../../actions/favActions';
import { IFavoritesProfileGridViewProps } from './types/FavoritesProfileGridView';
import useMediaBreakpoints from '../../../hooks/useMediaBreakpoints';
import breakpoints from '../../../theme/material/breakpoints';
import { mixpanelTrackEvent } from '../../../utils/mixpanel/common';

const ResultItem = styled('div')(() => ({
  ...getTextColor().DARKER,
  display: 'inline-flex',
  alignItems: 'center',
  flexDirection: 'column',
  marginTop: 24,
  marginBottom: 24,
  position: 'relative',
  flexBasis: 'calc(25% - 15px)',
  padding: '0 10px',
  gap: 12,

  [breakpoints.up(700)]: {
    '&:nth-child(4n + 1)': {
      paddingLeft: 0,
    },
    '&:nth-child(4n)': {
      paddingRight: 0,
    },
  },
  [breakpoints.between(589, 700)]: {
    flexBasis: 'calc(33.33% - 15px)',
    '&:nth-child(3n + 1)': {
      paddingLeft: 0,
    },
    '&:nth-child(3n)': {
      paddingRight: 0,
    },
    [breakpoints.down(640)]: {
      flexBasis: 'calc(33.33% - 20px)',
      padding: '0 10px !important',
    },
  },
  [breakpoints.down(639)]: {
    margin: '10px 0px',
    gap: 4,
  },
  [breakpoints.down(590)]: {
    flexBasis: 'calc(50% - 20px)',
    padding: '0 10px !important',
  },
}));

const FavoritesProfileGridView = ({
  favKey,
  profile,
  hashId,
  isFavorite,
  isFavoritedMe,
  isMatched,
  page,
  offer,
  pageTitle,
  enableSlider,
}: IFavoritesProfileGridViewProps): ReactElement => {
  const dispatch = useDispatch();

  const { trackProfileView, setMixpanelDataByKeyHandler } = useMixPanel();
  const { isMobile } = useMediaBreakpoints();

  const profileData = profile.profile.data;
  const extraInfo = page === 'to' ? 'favoritedYou' : 'yourFavorites';
  const avatar = useMemo(() => {
    return (
      lazilyEvaluateVal(profileData, 3, false, 'avatar', 'data', 'urls') && renderAvatar(profileData.avatar.data.urls)
    );
  }, [profileData.avatar]);

  const age = profileData.age;
  const city = profileData.city;
  const username = profile.username ? profile.username : '';
  const photoCount = parseInt(profileData.approved_public_photos_count);
  const privatePhotoCount = profileData.private_photos_count ? parseInt(profileData.private_photos_count) : 0;
  const online = profileData.online;
  const dataKey = `fav_${favKey}`;
  const userVerified = profileData.id_verified;
  const isSelfieVerified = profileData.selfie_verified;

  const trackCardClickEvent = () => {
    vysionTrack({
      action: 'click',
      component: 'Profile Card',
      extraInfo,
      page: 'Favorites',
    });

    if (page === 'viewed_me' && !enableSlider && isMobile) {
      trackProfileView({ Source: '2Col Viewed Me', User: hashId });
    } else {
      trackProfileView({ Source: pageTitle, User: hashId });
    }

    let mxOfferMadeSourceValue = TRACK_PROFILE_VIEWS_FOR_OFFER_MADE.profileViewDirect;

    if (pageTitle === 'Viewed Me') {
      mxOfferMadeSourceValue = TRACK_PROFILE_VIEWS_FOR_OFFER_MADE.profileViewViewedMe;
    } else if (pageTitle === 'Favorited Me') {
      mxOfferMadeSourceValue = TRACK_PROFILE_VIEWS_FOR_OFFER_MADE.profileViewFavoritedMe;
    } else if (pageTitle === 'My Favorites') {
      mxOfferMadeSourceValue = TRACK_PROFILE_VIEWS_FOR_OFFER_MADE.profileViewMyFavorites;
    }

    setMixpanelDataByKeyHandler({
      key: EVENT_NAMES.OFFER_MADE,
      payload: { source: mxOfferMadeSourceValue },
    });
  };

  const toggleTracking = () => {
    if (isFavorite) {
      dispatch(storeFavChange(favKey, page));
      dispatch(favToggle(hashId));
      return;
    }

    vysionTrack({
      action: 'click',
      component: isFavorite ? 'unfavorite' : 'favorite',
      extraInfo,
      page: 'Favorites',
    });

    // when user favorites
    // check if capable of making offer
    dispatch(storeFavChange(favKey, page));
    dispatch(favToggle(hashId));
    mixpanelTrackEvent({
      eventName: EVENT_NAMES.PROFILE_FAVORITED,
      payload: {
        eventProperties: {
          Source: pageTitle,
          User: hashId,
        },
        incrementalProperties: {
          '# favorites': 1,
        },
      },
    });
  };

  return (
    <ResultItem className="css-favorite-list-item grid-list-item">
      <FavoriteCard
        key={dataKey}
        age={age}
        city={city}
        username={username}
        hashId={hashId}
        photoCount={photoCount}
        privatePhotoCount={privatePhotoCount}
        online={online}
        imgUrl={avatar}
        cardWidth={ternaryFunc(isMobile, '100%', '100%')}
        cardMargin={ternaryFunc(isMobile, '5px 1%', '.5%')}
        detailsPadding={ternaryFunc(isMobile, '5px', '10px')}
        isVerified={userVerified}
        isSelfieVerified={isSelfieVerified}
        onClick={trackCardClickEvent}
        favorited={isFavorite === true}
        favoritedMe={isFavoritedMe}
        isMatched={isMatched}
        page={page}
        offer={offer}
        enableSlider={enableSlider}
        hideBorder={true}
      />
      <FavoriteActionButton
        favorited={isFavorite}
        toggleTracking={toggleTracking}
        page={page}
        offer={offer}
        otherAccount={profile}
        avatar={avatar}
        isMatched
        favKey={favKey}
        viewType="grid"
        isSuggestRequest={offer && offer.data && offer.data.suggest}
        pageTitle={pageTitle}
        enableSlider={false}
      />
    </ResultItem>
  );
};

export default memo(FavoritesProfileGridView);
